// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useSaveRfp } from './hooks/useHotelManager';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
}

export const RfpFormModal: React.FC<Props> = ({ open, onClose, eventId }) => {
  const saveRfp = useSaveRfp(eventId);

  const [form, setForm] = useState({
    hotel_name: '',
    hotel_address: '',
    hotel_city: '',
    hotel_state: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    rooms_requested: '',
    check_in_date: '',
    check_out_date: '',
    desired_rate: '',
    amenities_required: '',
    notes: '',
  });

  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.hotel_name.trim()) {
      toast.error('Hotel name is required');
      return;
    }

    try {
      await saveRfp.mutateAsync({
        data: {
          event_id: eventId,
          hotel_name: form.hotel_name.trim(),
          hotel_address: form.hotel_address || null,
          hotel_city: form.hotel_city || null,
          hotel_state: form.hotel_state || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          rooms_requested: form.rooms_requested ? parseInt(form.rooms_requested) : null,
          check_in_date: form.check_in_date || null,
          check_out_date: form.check_out_date || null,
          desired_rate: form.desired_rate ? parseFloat(form.desired_rate) : null,
          amenities_required: form.amenities_required
            ? form.amenities_required.split(',').map((a) => a.trim()).filter(Boolean)
            : [],
          notes: form.notes || null,
          status: 'sent',
        },
      });
      toast.success('RFP request saved');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save RFP');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New RFP Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Hotel Name *</Label>
            <Input value={form.hotel_name} onChange={(e) => set('hotel_name', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input value={form.hotel_address} onChange={(e) => set('hotel_address', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={form.hotel_city} onChange={(e) => set('hotel_city', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>State</Label>
            <Input value={form.hotel_state} onChange={(e) => set('hotel_state', e.target.value)} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Contact Name</Label>
              <Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Phone</Label>
              <Input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Rooms Requested</Label>
              <Input type="number" value={form.rooms_requested} onChange={(e) => set('rooms_requested', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Check-in</Label>
              <Input type="date" value={form.check_in_date} onChange={(e) => set('check_in_date', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Check-out</Label>
              <Input type="date" value={form.check_out_date} onChange={(e) => set('check_out_date', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Desired Rate/Night</Label>
            <Input type="number" step="0.01" value={form.desired_rate} onChange={(e) => set('desired_rate', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Amenities Required (comma-separated)</Label>
            <Input value={form.amenities_required} onChange={(e) => set('amenities_required', e.target.value)} placeholder="Pool, Breakfast, Parking" />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saveRfp.isPending}>
            {saveRfp.isPending ? 'Saving...' : 'Save RFP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
